import React, { useCallback, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useDispatch, useSelector } from 'react-redux';

import { RootState, AppDispatch } from '@/store';
import { getUnassignedCalls } from '@/store/slices/callReviewSlice';
import { UnassignedCall } from '@/types/api';
import { colors, font, space, radius, shadow } from '@/theme/chalkCourt';
import AssigneePickerModal from '@/components/callperformance/AssigneePickerModal';
import ExcludeCallModal from '@/components/callperformance/ExcludeCallModal';

/**
 * Admin screen: calls the ops-advisor pipeline couldn't attribute to a staff
 * member. Tap a row to open the call detail, or "Assign" to pick the staff member
 * inline — on assign the call drops out of the list. Gated to admins (the entry
 * is hidden for non-admins and the backend 403s).
 */
const UnassignedCallsScreen: React.FC = () => {
  const navigation = useNavigation<NativeStackNavigationProp<any>>();
  const dispatch = useDispatch<AppDispatch>();
  const { unassigned, unassignedLoading, unassignedError, unassignedAvailable } = useSelector(
    (s: RootState) => s.callReview
  );

  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const [excludeFor, setExcludeFor] = useState<string | null>(null);

  useFocusEffect(
    useCallback(() => {
      dispatch(getUnassignedCalls());
    }, [dispatch])
  );

  const renderRow = (c: UnassignedCall) => (
    <View key={c.callUuid} style={styles.card}>
      <TouchableOpacity
        style={styles.rowMain}
        activeOpacity={0.7}
        onPress={() => navigation.navigate('CallDetail', { callUuid: c.callUuid })}>
        <View style={styles.rowHeader}>
          {!!c.callType && (
            <View style={styles.typeBadge}>
              <Text style={styles.typeBadgeText}>{c.callType}</Text>
            </View>
          )}
          <Text style={styles.time}>{c.startTimeDisplay}</Text>
          {!!c.locationCode && <Text style={styles.loc}>{c.locationCode}</Text>}
        </View>
        <Text style={styles.numbers}>
          {c.fromNumber || '—'} → {c.toNumber || '—'}
          {c.durationLabel ? ` · ${c.durationLabel}` : ''}
        </Text>
      </TouchableOpacity>
      <View style={styles.actions}>
        <TouchableOpacity style={styles.assignBtn} onPress={() => setPickerFor(c.callUuid)}>
          <Text style={styles.assignBtnText}>Assign</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.excludeBtn} onPress={() => setExcludeFor(c.callUuid)}>
          <Text style={styles.excludeBtnText}>Exclude</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.content}
        refreshControl={
          <RefreshControl
            refreshing={unassignedLoading}
            onRefresh={() => dispatch(getUnassignedCalls())}
            tintColor={colors.accent}
          />
        }>
        {unassignedLoading && unassigned.length === 0 ? (
          <View style={styles.centered}>
            <ActivityIndicator size="large" color={colors.accent} />
          </View>
        ) : unassignedError ? (
          <View style={styles.centered}>
            <Text style={styles.stateTitle}>Couldn't load unassigned calls</Text>
            <Text style={styles.stateBody}>{unassignedError}</Text>
          </View>
        ) : !unassignedAvailable ? (
          <View style={styles.centered}>
            <Text style={styles.stateTitle}>Unavailable</Text>
            <Text style={styles.stateBody}>Call review isn't reachable right now.</Text>
          </View>
        ) : unassigned.length === 0 ? (
          <View style={styles.centered}>
            <Text style={styles.stateTitle}>All caught up</Text>
            <Text style={styles.stateBody}>No unassigned calls in the last 10 days.</Text>
          </View>
        ) : (
          <>
            <Text style={styles.count}>{unassigned.length} unassigned</Text>
            {unassigned.map(renderRow)}
          </>
        )}
      </ScrollView>

      <AssigneePickerModal
        visible={pickerFor != null}
        callUuid={pickerFor}
        onClose={() => setPickerFor(null)}
        onAssigned={() => {
          /* slice removes the assigned call from the list */
        }}
      />

      <ExcludeCallModal
        visible={excludeFor != null}
        callUuid={excludeFor}
        onClose={() => setExcludeFor(null)}
        onExcluded={() => {
          /* slice removes the excluded call from the list */
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.bg },
  content: { padding: space.lg, paddingBottom: space.xxl },
  centered: { alignItems: 'center', justifyContent: 'center', paddingVertical: space.xxl, gap: space.sm },
  stateTitle: { fontFamily: font.displayBold, fontSize: 20, color: colors.ink, textAlign: 'center' },
  stateBody: { fontFamily: font.body, fontSize: 15, color: colors.inkSoft, textAlign: 'center' },
  count: {
    fontFamily: font.bodySemi,
    fontSize: 13,
    color: colors.inkFaint,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: space.sm,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.sm,
    ...shadow.card,
  },
  rowMain: { flex: 1 },
  rowHeader: { flexDirection: 'row', alignItems: 'center', gap: space.sm },
  typeBadge: {
    paddingHorizontal: space.sm,
    paddingVertical: 2,
    borderRadius: radius.sm,
    backgroundColor: colors.surfaceSunk,
    borderWidth: 1,
    borderColor: colors.line,
  },
  typeBadgeText: { fontFamily: font.bodyMed, fontSize: 12, color: colors.inkSoft },
  time: { fontFamily: font.bodySemi, fontSize: 14, color: colors.ink },
  loc: { fontFamily: font.bodyMed, fontSize: 12, color: colors.inkFaint },
  numbers: { fontFamily: font.body, fontSize: 13, color: colors.inkSoft, marginTop: space.xs },
  actions: {
    marginLeft: space.md,
    gap: space.xs,
  },
  assignBtn: {
    paddingHorizontal: space.lg,
    paddingVertical: space.sm,
    borderRadius: radius.pill,
    backgroundColor: colors.accent,
    alignItems: 'center',
  },
  assignBtnText: { fontFamily: font.bodyBold, fontSize: 14, color: colors.onAccent },
  excludeBtn: {
    paddingHorizontal: space.lg,
    paddingVertical: space.sm,
    borderRadius: radius.pill,
    borderWidth: 1,
    borderColor: colors.lineStrong,
    alignItems: 'center',
  },
  excludeBtnText: { fontFamily: font.bodySemi, fontSize: 14, color: colors.inkSoft },
});

export default UnassignedCallsScreen;
